import { doc, getDoc, type Firestore } from 'firebase/firestore'

/**
 * uid -> display name for the review trail. Reads `users/{uid}` one doc at a
 * time (there is no members projection to read instead) and degrades: a uid
 * we cannot read is simply absent from the map, and every caller falls back
 * to the raw uid rather than showing an empty author.
 *
 * MYAC-1160 review pass — extracted from the two identical copies in
 * `regulatoryMonitoringExports.ts` (the legacy and the v2 PDF paths) so the
 * screen's « Revu par … » and the PDF's cannot resolve a name differently.
 */
export async function fetchReviewerNames(
  db: Firestore,
  uids: Iterable<string>,
): Promise<Map<string, string>> {
  const names = new Map<string, string>()
  const unique = Array.from(new Set(Array.from(uids).filter((u) => !!u)))
  await Promise.all(
    unique.map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, 'users', uid))
        if (!snap.exists()) return
        const data = snap.data()
        const name = data.display_name || data.email || uid
        if (typeof name === 'string' && name) names.set(uid, name)
      } catch {
        // Unreadable (rules, offline) — the caller falls back to the uid.
      }
    }),
  )
  return names
}
